import {DEFAULT_SKILLS, humanizeName} from "./skills";
import {isPresentationRecord, presentedFields, type ToolPresentation} from "./toolPresentation";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Stable operation identities select UI copy, independently of the server's fallback text. */
const campaignCopy: Readonly<Record<string, string>> = {
   campaigns_start: "toolApproval.campaign.start",
   campaigns_stop: "toolApproval.campaign.stop",
   campaigns_archive: "toolApproval.campaign.archive",
};

export function knownToolActivity(toolName: string, input: unknown, t: Translate) {
   if (toolName === "load_skill" || toolName === "load_gail_skill") {
      const skillName = isPresentationRecord(input) && typeof input.skillName === "string" ? input.skillName.trim() : "";
      const preset = Object.hasOwn(DEFAULT_SKILLS, skillName) ? DEFAULT_SKILLS[skillName] : undefined;
      const name = preset ? t(`skills.${preset.key}.title`) : humanizeName(skillName);
      return {icon: "wrench" as const,
         inProgressText: name ? t("tools.phrases.loadingSkillNamed", {name}) : t("tools.phrases.activatingSkill"),
         completedText: name ? t("tools.phrases.loadedSkillNamed", {name}) : t("tools.phrases.activatedSkill")};
   }
   const key = Object.hasOwn(campaignCopy, toolName) ? campaignCopy[toolName] : undefined;
   if (key) return {icon: "wrench" as const, inProgressText: t(`${key}.title`), completedText: t(`${key}.title`)};
}

/** Render-only projection. Never write translated copy into the frozen approval or request. */
export function displayToolPresentation(presentation: ToolPresentation, input: unknown, t: Translate): ToolPresentation {
   if (presentation.toolName === "phone_scripts_create") {
      const fields = presentedFields(input, presentation.fields);
      const names = ["name", "agentName", "direction", "greeting", "goodbye", "useCaseAssociations"];
      const property = (path: string[]) => path.length === 1 ? path[0] : path.length === 2 && path[0] === "body" ? path[1] : "";
      return {...presentation, icon: "document",
         title: {text: t("toolApproval.createScript.title")},
         approveLabel: {text: t("toolApproval.createScript.approve")},
         description: {text: t("toolApproval.createScript.description")},
         fields: [...fields].sort((a, b) => {
            const rank = (path: string[]) => {const index = names.indexOf(property(path)); return index < 0 ? names.length : index;};
            return rank(a.path) - rank(b.path);
         }).map(({value: _value, ...field}) => {
            const name = property(field.path);
            if (!names.includes(name)) return field;
            return {...field, label: {text: t(`toolApproval.createScript.${name}`)},
               ...(name === "name" ? {emphasis: "primary" as const} : {}),
               ...(["greeting", "goodbye"].includes(name) ? {format: "multiline" as const} : {}),
            };
         }),
      };
   }
   const key = Object.hasOwn(campaignCopy, presentation.toolName) ? campaignCopy[presentation.toolName] : undefined;
   if (!key) return presentation;
   return {...presentation,
      title: {text: t(`${key}.title`)},
      description: {text: t(`${key}.description`)},
      approveLabel: {text: t(`${key}.approve`)},
      fields: presentedFields(input, presentation.fields).map(({value: _value, ...field}) =>
         field.path.length === 1 && field.path[0] === "id"
            ? {...field, label: {text: t("toolApproval.entities.campaign")}, format: "entity", emphasis: "primary"}
            : field),
   };
}
